import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import styles from "./CalendarDropdown.module.css";
import { he, enUS } from "date-fns/locale";
import i18n from "../../../i18n";

const CalendarDropdown: React.FC<{
  title: string,
  startDate: Date | undefined;
  endDate: Date | undefined;
  setStartDate: Dispatch<SetStateAction<Date | undefined>>;
  setEndDate: Dispatch<SetStateAction<Date | undefined>>;
  className: string
}> = ({ title,
  setStartDate,
  setEndDate,
  startDate,
  endDate,
  className
}) => {
    // Get current language and locale
    const currentLang = i18n.language || 'he';
    
    // Map i18n language codes to date-fns locales
    const getDateFnsLocale = () => {
      switch (currentLang) {
        case 'en-US':
        case 'en':
          return enUS;
        case 'he':
        default:
          return he;
      }
    };
    
    // Map i18n language codes to toLocaleDateString locale strings
    const getLocaleString = () => {
      switch (currentLang) {
        case 'en-US':
        case 'en':
          return 'en-US';
        case 'he':
        default:
          return 'he-IL';
      }
    };
    
    // Check if current language is RTL
    const isRTL = currentLang === 'he';
    const dateFnsLocale = getDateFnsLocale();
    const localeString = getLocaleString();
    
    const [showCalendar, setShowCalendar] = useState(title === "");
    const [tab, setTab] = useState<"from" | "to">("from");
    const [startInput, setStartInput] = useState(new Date().toLocaleDateString(localeString, { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '/'));
    const [localStartDate, setLocalStartDate] = useState(startDate);
    const [endInput, setEndInput] = useState(new Date().toLocaleDateString(localeString, { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '/'));
    const [startInputError, setStartInputError] = useState<string>("");
    const [endInputError, setEndInputError] = useState<string>("");
    const [month, setMonth] = useState<Date>(localStartDate ? localStartDate : new Date(new Date().setMonth(new Date().getMonth() - 6)));


    const today = new Date();
    today.setHours(23, 59, 59, 999);

    useEffect(() => {
      setLocalStartDate(startDate)
    }, [startDate]);

    useEffect(() => {
      setStartInput(localStartDate ? localStartDate.toLocaleDateString(localeString, { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '/') : new Date().toLocaleDateString(localeString, { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '/'));
    }, [localStartDate, localeString]);

    useEffect(() => {
      setEndInput(endDate ? endDate.toLocaleDateString(localeString, { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '/') : new Date().toLocaleDateString(localeString, { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '/'));
    }, [endDate, localeString]);

    const handleDateSelect = (date?: Date) => {
      if (date && date > today) {
        return;
      }

      if (tab === "from") {
        setLocalStartDate(date);
        setStartInputError("");
        if (endDate && date && date > endDate) {
          setEndDate(undefined);
        }
        setTab("to");
        setMonth(endDate ? endDate : new Date())
      }
      else {
        if (!localStartDate || !date || date >= localStartDate) {
          setStartDate(localStartDate);
          setEndDate(date);
          setEndInputError("");
          if (localStartDate) setShowCalendar(false);
          else {
            setTab("from");
            setMonth(localStartDate ? localStartDate : new Date(new Date().setMonth(new Date().getMonth() - 6)))
          }
        }
      };
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'from' | 'to') => {
      const value = e.target.value;

      if (type === 'from') {
        setStartInput(value);
        setStartInputError("");
      } else {
        setEndInput(value);
        setEndInputError("");
      }

      const parts = value.split(/[-./]/);
      if (parts.length === 3) {
        const [day, month, year] = parts.map(p => parseInt(p, 10));
        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && year > 1000 && day > 0 && day <= 31 && month > 0 && month <= 12) {
          const newDate = new Date(year, month - 1, day);
          if (!isNaN(newDate.getTime())) {
            if (newDate > today) {
              if (type === 'from') {
                setStartInputError(t('CANNOT_SELECT_FUTURE_DATE'));
              } else {
                setEndInputError(t('CANNOT_SELECT_FUTURE_DATE'));
              }
              return;
            }

            if (type === 'from') {
              // setStartDate(newDate);
              setLocalStartDate(newDate)
              setStartInputError("");
              if (endDate && newDate > endDate) {
                setEndDate(undefined);
              }
              if (!endDate) {
                setTab('to');
                setMonth(endDate ? endDate : new Date())

              }
            } else {
              if (localStartDate && newDate < localStartDate) {
                setEndInputError(t('END_DATE_MUST_BE_LATER'));
                return;
              }
              setEndDate(newDate);
              setEndInputError("");
              if (localStartDate) {
                setShowCalendar(false);
              }
            }
          } else {
            if (type === 'from') {
              setStartInputError(t('INVALID_DATE_FORMAT'));
            } else {
              setEndInputError(t('INVALID_DATE_FORMAT'));
            }
          }
        } else {
          if (type === 'from') {
            setStartInputError(t('INVALID_DATE'));
          } else {
            setEndInputError(t('INVALID_DATE'));
          }
        }
      }
    };

    return (
      <div className={`${styles.calendar_dropdown} `}>
        {title !== "" && (<><div className={styles.comboBoxTitleContainer}>
          <p className={className}>{title}</p>
          <button
            className={styles.comboBoxCloseBtn}
            onClick={() => {
              setStartDate(undefined);
              setEndDate(undefined);
              setShowCalendar(false);
              setTab("from");
              setMonth(new Date(localStartDate ? localStartDate : new Date(new Date().setMonth(new Date().getMonth() - 6))));
            }}
          >
            <span className={styles.selectAllIcon}>
              {/* X SVG */}
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M6 6L14 14M14 6L6 14" stroke="#0066cc" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
          </button>
        </div>
          <button
            className={`comboBox2 ${styles.comboBox}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowCalendar((prev) => !prev);
            }}
          >
            {(localStartDate && endDate) ?
              `${localStartDate?.toLocaleDateString(localeString)}-${endDate?.toLocaleDateString(localeString)}` :
              t('CHOOSE_DATE_RANGE')}
          </button></>)}

        {showCalendar && (
          <div
            className={styles.overlay}
            onClick={() => setShowCalendar(false)}
          >
            <div
              className={styles["calendar-panel"]}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles["calendar-tabs"]}>
                <button
                  className={`${styles.fromToButton} ${tab === "from" ? styles.active : ""}`}
                  onClick={(e) => {
                    setMonth(localStartDate ? localStartDate : new Date(new Date().setMonth(new Date().getMonth() - 6)));
                    ;
                    e.stopPropagation(); setTab("from")
                  }}
                >
                  {t('FROM_DATE')}</button>
                <button
                  className={`${styles.fromToButton} ${tab === "to" ? styles.active : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMonth(endDate ? endDate : new Date());
                    setTab("to")
                  }}
                >
                  {t('TO_DATE')}</button>
              </div>
              <div className={styles.dateInputs}>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    placeholder="dd/mm/yyyy"
                    value={startInput}
                    onChange={(e) => handleInputChange(e, 'from')}
                    onFocus={() => {
                      setMonth(localStartDate ? localStartDate : new Date(new Date().setMonth(new Date().getMonth() - 6)));
                      setTab('from')
                    }}
                    className={`${styles.dateInput} ${startInputError ? styles.inputError : ''}`}
                  />
                  {startInputError && (
                    <div className={styles.errorMessage}>{startInputError}</div>
                  )}
                </div>
                <span className={styles.dateSeparator}>-</span>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    placeholder="dd/mm/yyyy"
                    value={endInput}
                    onChange={(e) => handleInputChange(e, 'to')}
                    onFocus={() => {
                      setTab('to');
                      setMonth(endDate ? endDate : new Date());

                    }}
                    className={`${styles.dateInput} ${endInputError ? styles.inputError : ''}`}
                  />
                  {endInputError && (
                    <div className={styles.errorMessage}>{endInputError}</div>
                  )}
                </div>
              </div>
              <DayPicker
                dir={isRTL ? "rtl" : "ltr"}
                mode="single"
                selected={tab === "from" ? localStartDate : endDate}
                onSelect={handleDateSelect}
                locale={dateFnsLocale}
                month={month}
                onMonthChange={setMonth}
                disabled={[
                  { after: today },
                  ...(tab === "to" && localStartDate
                    ? [{ before: localStartDate }]
                    : tab === "from" && endDate
                      ? [{ after: endDate }]
                      : [])
                ]}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

export default CalendarDropdown;
